import superagent from 'superagent';
import sels from 'sels';

import {
  ERRORS,
  DATE_SLUG_ENCODED,
  HEADERS_SLUG_ENCODED,
  CACHE_CONTROL_VERSION_SLUG_ENCODED,
  HEADER_KEY,
  SEPARATOR,
} from './constants';

import { dispatch } from './limiter/dispatch';

import { showCaptcha } from './limiter/redux/actions';
import { hardCodedKey, buildMessage, hexDecode } from './helpers/encrypting';
import createInterceptor from './helpers/createInterceptor';

const methods = ['get', 'post', 'del', 'put', 'patch', 'head'] as const;
export type Method = typeof methods extends readonly (infer U)[] ? U : never;

type RequestFactory = (url: string, options?: Options) => superagent.SuperAgentRequest;
type SuperAgentFacade = Record<Method, RequestFactory>;
type ExtendedGlobal = typeof globalThis & { [DATE_SLUG_ENCODED]: string };

export type Options = {
  hasLimiterInterceptor?: boolean;
  captchaMountContainer?: string;
};

const getCookieHeader = () => sels.get(HEADER_KEY) || '';

const parseCookieHeader = (cookieHeader: string) => {
  try {
    return atob(cookieHeader)
      .split(SEPARATOR)
      .reduce<Record<string, string>>((acc, value) => {
        const separatorIndex = value.indexOf('=');

        if (separatorIndex !== -1) {
          return {
            ...acc,
            [value.slice(0, separatorIndex)]: value.slice(separatorIndex + 1),
          };
        }

        return acc;
      }, {});
  } catch (error) {
    return {};
  }
};

const formatCookieHeader = (cookieObject: Record<string, string>) => {
  return btoa(
    Object.entries(cookieObject)
      .map(([key, value]) => `${key}=${value}`)
      .join(SEPARATOR)
  );
};

const limiterInterceptor = (options: Options, reqTime: number) =>
  createInterceptor(async (error, response) => {
    if (response?.body?.error !== 'ATTEMPTS_EXCEEDED' || !dispatch) {
      return true;
    }

    const {
      body: {
        result: { success: successUrl, keys },
      },
    } = response;

    const { captchaMountContainer: mountContainer } = options;

    try {
      /**
       * Этот dispatch инициализируется в компоненте <Captcha />
       * (src/utilities/superagent/src/limiter/Captcha.tsx)
       *
       * Если компонента не окажется в рендер-дереве, интерцептор работать не будет!
       */

      await dispatch(
        showCaptcha({
          reqTime,
          mountContainer,
          successUrl,
          keys,
        })
      );
    } catch (showCaptchError) {
      console.error('[captcha]', showCaptchError);

      /**
       * В случае если завалилась капча наверное лучше просто
       * все перезагрузить
       */
      window.location.reload();

      return false;
    }

    // Более красиво не придумал как ретрай сделать
    // Обращение к внутренним свойствам superagent

    // @ts-ignore
    // eslint-disable-next-line no-underscore-dangle
    await response.req._retry();

    return false;
  });

const defaultOptions: Options = {
  hasLimiterInterceptor: true,
  captchaMountContainer: '',
};

const getSuperagent = (method: Method, url: string, options: Options = {}) => {
  const makeRequest = superagent[method];

  const { hasLimiterInterceptor } = {
    ...defaultOptions,
    ...options,
  } as Required<Options>;

  let request = makeRequest(url);

  if (hasLimiterInterceptor) {
    request = request.use(limiterInterceptor(options, Date.now()));
  }

  const cookieHeader = getCookieHeader();

  if (cookieHeader) {
    request = request.set(HEADER_KEY, cookieHeader);
  }

  return request
    .set(
      hexDecode(hardCodedKey, CACHE_CONTROL_VERSION_SLUG_ENCODED),
      (global as ExtendedGlobal)[DATE_SLUG_ENCODED]
    )
    .on('response', (response) => {
      const { headers, body } = response;

      // доступ к данным заголовков получаем косвенным образом, чтобы нельзя было поиском по коду найти названия ключей
      // для этого используем простое шифрование
      // Далее контрольную сумму сетим в глобальную переменную
      (global as ExtendedGlobal)[DATE_SLUG_ENCODED] = buildMessage(
        // @ts-ignore Тут проще просто заигнорить
        response[hexDecode(hardCodedKey, HEADERS_SLUG_ENCODED)][
          hexDecode(hardCodedKey, DATE_SLUG_ENCODED)
        ]
      );

      try {
        const encodeString = headers[HEADER_KEY] || headers[HEADER_KEY.toLowerCase()];
        const newCookies = parseCookieHeader(encodeString);
        const currentCookies = parseCookieHeader(getCookieHeader());

        sels.set(
          HEADER_KEY,
          formatCookieHeader({
            ...currentCookies,
            ...newCookies,
          })
        );
      } catch (error) {
        console.error('[superagent]', error);
      }

      if (!body?.error) {
        return;
      }

      const {
        error: { type, redirectLink },
      } = body;

      switch (type) {
        case ERRORS.NEED_REDIRECT: {
          /* для корректного отрабатывания всех сайд-эффектов и обработчиков
          устанавливается setTimeout для выполнения редиректа в следующей фазе event loop */
          setTimeout(() => {
            window.location.href = redirectLink;
          }, 0);

          break;
        }

        default:
          break;
      }
    });
};

const ottSuperagent = methods.reduce(
  (acc, method) => ({
    ...acc,
    [method]: (url: string, options?: Options) => getSuperagent(method, url, options),
  }),
  {} as SuperAgentFacade
);

export default ottSuperagent;
